import * as yup from 'yup';
import { IFormElement } from '../types';

export const buildFieldValidation = (element: IFormElement): yup.AnySchema => {
  const {
    type,
    inputType,
    isRequired,
    readOnly,
    minLength,
    maxLength,
    choices,
  } = element;

  if ((type === 'text' && inputType !== 'number') || type === 'comment') {
    let schema: yup.StringSchema = yup.string();

    if (isRequired && !readOnly) {
      schema = schema.required(
        `${element.title || 'Este campo'} é obrigatório`
      );

      if (minLength) {
        schema = schema.min(minLength, `Mínimo de ${minLength} caracteres`);
      }

      if (maxLength) {
        schema = schema.max(maxLength, `Máximo de ${maxLength} caracteres`);
      }

      if (inputType === 'email') {
        schema = schema.email('Email inválido');
      }

      if (inputType === 'url') {
        schema = schema.url('URL inválida');
      }
    }

    return schema;
  }

  if (type === 'text' && inputType === 'number') {
    let schema: yup.NumberSchema = yup.number().typeError('Deve ser um número');

    if (isRequired && !readOnly) {
      schema = schema.required(
        `${element.title || 'Este campo'} é obrigatório`
      );

      if (element.min !== undefined) {
        schema = schema.min(
          Number(element.min),
          `Valor mínimo: ${element.min}`
        );
      }

      if (element.max !== undefined) {
        schema = schema.max(
          Number(element.max),
          `Valor máximo: ${element.max}`
        );
      }
    }

    return schema;
  }

  if (type === 'dropdown' || type === 'radiogroup') {
    let schema: yup.StringSchema = yup.string();

    if (isRequired && !readOnly) {
      schema = schema.required(
        `${element.title || 'Este campo'} é obrigatório`
      );

      const elementChoices = choices as
        | string[]
        | { value: string; text: string }[];

      if (
        elementChoices &&
        elementChoices.length > 0 &&
        !element.showOtherItem
      ) {
        const formatedChoices = elementChoices.map((choice) => {
          return typeof choice === 'string'
            ? { value: choice, text: choice }
            : choice;
        });

        const choicesValues = formatedChoices.map((choice) => choice.value);
        schema = schema.oneOf(choicesValues, 'Selecione uma opção válida');
      }
    }

    return schema;
  }

  if (type === 'checkbox' || type === 'tagbox') {
    let schema = yup.array().of(yup.string());

    if (isRequired && !readOnly) {
      schema = schema.min(1, `${element.title || 'Este campo'} é obrigatório`);
    }

    return schema;
  }

  if (type === 'boolean') {
    let schema: yup.BooleanSchema = yup.boolean();

    if (isRequired && !readOnly) {
      schema = schema.required(
        `${element.title || 'Este campo'} é obrigatório`
      );
    }

    return schema;
  }

  if (type === 'file') {
    const fileShape = yup.object({
      name: yup.string().required('Nome do arquivo é obrigatório'),
      type: yup.string().required('Tipo do arquivo é obrigatório'),
      content: yup
        .string()
        .matches(
          /^data:.+;base64,.+/,
          'O arquivo deve estar em formato base64 (data URL)'
        )
        .required('O conteúdo do arquivo é obrigatório'),
    });

    let schema: yup.AnySchema;
    if (isRequired && !readOnly) {
      schema = yup
        .array()
        .of(fileShape)
        .min(1, `${element.title || 'Este campo'} é obrigatório`)
        .default([]);
    } else {
      schema = yup.array().of(fileShape).default([]);
    }

    return schema;
  }

  if (type === 'imagepicker') {
    let schema: yup.AnySchema = yup.mixed();

    if (isRequired && choices && choices.length > 0) {
      const elementChoices = choices as {
        value: string;
        imageLink: string;
        text?: string;
      }[];
      const choicesValues = elementChoices.map((choice) => choice.value);

      if (element.multiSelect) {
        schema = yup
          .array()
          .of(yup.string().oneOf(choicesValues, 'Selecione opções válidas'))
          .min(1, `${element.title || 'Este campo'} é obrigatório`);
      } else {
        schema = yup
          .string()
          .oneOf(choicesValues, 'Selecione uma opção válida')
          .required(`${element.title || 'Este campo'} é obrigatório`);
      }
    }

    return schema;
  }

  if (type === 'rating') {
    let schema: yup.NumberSchema<number | null | undefined> = yup
      .number()
      .nullable()
      .typeError('Deve ser um número');

    if (isRequired && !readOnly) {
      schema = schema.required(
        `${element.title || 'Este campo'} é obrigatório`
      );
    }

    return schema;
  }

  if (type === 'ranking') {
    let schema = yup.array().of(yup.string());

    if (isRequired && !readOnly) {
      schema = schema.min(1, `${element.title || 'Este campo'} é obrigatório`);
      if (choices && choices.length > 0) {
        const elementChoices = choices as
          | string[]
          | { value: string; text: string }[];
        const choicesValues = elementChoices.map((choice) =>
          typeof choice === 'string' ? choice : choice.value
        );

        schema = schema.of(
          yup.string().oneOf(choicesValues, 'Selecione opções válidas')
        );
      }
    }

    return schema;
  }

  if (type === 'matrix') {
    let schema: yup.ObjectSchema<any> = yup.object();

    if (!readOnly) {
      if (isRequired || element.isAllRowRequired) {
        schema = schema.test(
          'matrix-required-all',
          `${element.title || 'Este campo'} é obrigatório`,
          (value: Record<string, any> | undefined | null) => {
            if (!value || typeof value !== 'object') return false;
            for (const key of Object.keys(value)) {
              if (value[key] === '' || value[key] == null) {
                return false;
              }
            }
            return true;
          }
        );
      }

      if (element.eachRowUnique) {
        schema = schema.test(
          'matrix-each-row-unique',
          'Cada linha deve ter uma resposta diferente.',
          (value: Record<string, any> | undefined | null) => {
            if (!value || typeof value !== 'object') return true;
            const values = Object.values(value);
            const filteredValues = values.filter((v) => v !== '' && v != null);
            const set = new Set(filteredValues);
            return set.size === filteredValues.length;
          }
        );
      }
    }

    return schema;
  }

  if (type === 'matrixdropdown') {
    const schema: yup.ObjectSchema<any> = yup
      .object()
      .test(
        'matrixdropdown-required-cols',
        `${element.title || 'Este campo'} é obrigatório`,
        (value: Record<string, any> | undefined | null) => {
          if (!isRequired) return true;
          if (!value || typeof value !== 'object') return false;

          for (const rowKey of Object.keys(value)) {
            const row = value[rowKey];
            if (typeof row !== 'object' || !row) return false;
            for (const colKey of Object.keys(row)) {
              if (row[colKey] === null) return false;

              if (typeof row[colKey] === 'object') {
                if (
                  !Object.keys(row[colKey]).length ||
                  !(row[colKey] as number[] | string[]).length
                )
                  return false;
              } else if (typeof row[colKey] === 'string') {
                if (row[colKey] === '' || row[colKey] == null) return false;
              }
            }
          }

          return true;
        }
      );

    return schema;
  }

  if (type === 'matrixdynamic') {
    const schema: yup.ArraySchema<any, any, any, any> = yup
      .array()
      .of(yup.object().shape({}))
      .test(
        'matrixdynamic-required-cols',
        `${element.title || 'Este campo'} é obrigatório`,
        (value: any[] | undefined | null) => {
          if (!isRequired) return true;
          if (!value) return false;

          for (const item of value) {
            for (const itemKey of Object.keys(item)) {
              if (typeof item[itemKey] === 'object') {
                if (item[itemKey] === null) return false;

                if (
                  !Object.keys(item[itemKey]).length ||
                  !(item[itemKey] as number[] | string[]).length
                )
                  return false;
              } else if (typeof item[itemKey] === 'string') {
                if (item[itemKey] === '' || item[itemKey] == null) return false;
              }
            }
          }

          return true;
        }
      );

    return schema;
  }

  if (type === 'multipletext') {
    const schema: yup.ObjectSchema<any> = yup
      .object()
      .test(
        'multipletext-required-all',
        `${element.title || 'Este campo'} é obrigatório`,
        (value: Record<string, any> | undefined | null) => {
          if (!isRequired) return true;
          if (!value || typeof value !== 'object') return false;

          for (const key of Object.keys(value)) {
            if (value[key] === '' || value[key] == null) {
              return false;
            }
          }

          return true;
        }
      );

    return schema;
  }

  let schema: yup.StringSchema = yup.string();
  if (isRequired && !readOnly) {
    schema = schema.required(`${element.title || 'Este campo'} é obrigatório`);
  }
  return schema;
};
